import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  IconButton,
  Alert
} from '@mui/material';
import {
  Add,
  Delete,
  Close
} from '@mui/icons-material';
import UserPaymentMethodsService from '../../services/userPaymentMethods.service';

const UserPaymentMethodsModal = ({ open, onClose, onSave }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const availablePaymentMethods = [
    'Bank Transfer',
    'Mobile Money',
    'PayPal',
    'Cash',
    'Western Union',
    'MoneyGram',
    'Skrill',
    'Neteller',
    'Perfect Money',
    'Payoneer'
  ];

  const getPaymentDetailFields = (method) => {
    const fieldMap = {
      'Bank Transfer': [
        { key: 'bank_name', label: 'Bank Name' },
        { key: 'account_number', label: 'Account Number' },
        { key: 'account_name', label: 'Account Name' },
        { key: 'routing_number', label: 'Routing Number' },
        { key: 'swift_code', label: 'SWIFT Code' }
      ],
      'Mobile Money': [
        { key: 'provider', label: 'Provider' },
        { key: 'phone_number', label: 'Phone Number' },
        { key: 'account_name', label: 'Account Name' }
      ],
      'PayPal': [
        { key: 'email', label: 'PayPal Email' },
        { key: 'account_name', label: 'Account Name' }
      ],
      'Cash': [
        { key: 'location', label: 'Location' },
        { key: 'instructions', label: 'Instructions' }
      ],
      'Western Union': [
        { key: 'receiver_name', label: 'Receiver Name' },
        { key: 'country', label: 'Country' },
        { key: 'city', label: 'City' }
      ],
      'MoneyGram': [
        { key: 'receiver_name', label: 'Receiver Name' },
        { key: 'country', label: 'Country' },
        { key: 'city', label: 'City' }
      ],
      'Skrill': [
        { key: 'email', label: 'Skrill Email' },
        { key: 'account_name', label: 'Account Name' }
      ],
      'Neteller': [
        { key: 'email', label: 'Neteller Email' },
        { key: 'account_name', label: 'Account Name' }
      ],
      'Perfect Money': [
        { key: 'account_id', label: 'Account ID' },
        { key: 'account_name', label: 'Account Name' }
      ],
      'Payoneer': [
        { key: 'email', label: 'Payoneer Email' },
        { key: 'account_name', label: 'Account Name' }
      ]
    };
    return fieldMap[method] || [];
  };

  useEffect(() => {
    if (open) {
      loadPaymentMethods();
    }
  }, [open]);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await UserPaymentMethodsService.getPaymentMethods();
      if (response.status === 'success') {
        setPaymentMethods(response.payment_methods || []);
        setPaymentDetails(response.payment_details || {});
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodChange = (event, value) => {
    setPaymentMethods(value);
    // Reset payment details for removed methods
    const newDetails = { ...paymentDetails };
    Object.keys(newDetails).forEach(method => {
      if (!value.includes(method)) {
        delete newDetails[method];
      }
    });
    setPaymentDetails(newDetails);
  };

  const handlePaymentDetailChange = (method, field, value) => {
    setPaymentDetails(prev => ({
      ...prev,
      [method]: {
        ...prev[method],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate that all selected methods have details
      const missingDetails = paymentMethods.filter(method => {
        const details = paymentDetails[method] || {};
        const requiredFields = getPaymentDetailFields(method);
        return requiredFields.some(field => !details[field.key] || details[field.key].trim() === '');
      });

      if (missingDetails.length > 0) {
        setError(`Please fill in all required details for: ${missingDetails.join(', ')}`);
        return;
      }

      const response = await UserPaymentMethodsService.updatePaymentMethods(paymentMethods, paymentDetails);
      if (response.status === 'success') {
        onSave(response.user);
        onClose();
      }
    } catch (error) {
      console.error('Error saving payment methods:', error);
      setError('Failed to save payment methods');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Manage Payment Methods</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Select Payment Methods
          </Typography>
          <Autocomplete
            multiple
            options={availablePaymentMethods}
            value={paymentMethods}
            onChange={handlePaymentMethodChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Payment Methods"
                placeholder="Select payment methods"
              />
            )}
          />
        </Box>

        {paymentMethods.length > 0 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Payment Method Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Provide your account details for each payment method. This information will be shown to partners for payment.
            </Typography>
            
            {paymentMethods.map((method) => (
              <Card key={method} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    {method}
                  </Typography>
                  <Grid container spacing={2}>
                    {getPaymentDetailFields(method).map((field) => (
                      <Grid item xs={12} sm={6} key={field.key}>
                        <TextField
                          fullWidth
                          label={field.label}
                          value={paymentDetails[method]?.[field.key] || ''}
                          onChange={(e) => handlePaymentDetailChange(method, field.key, e.target.value)}
                          required
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || paymentMethods.length === 0}
        >
          {loading ? 'Saving...' : 'Save Payment Methods'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserPaymentMethodsModal;







